import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { refusalText } from "../api/errors";
import { useApiCommand } from "../api/hooks";
import type { NoteOfferBlock } from "../api/types";
import { Button } from "../ui/Button";
import { SafeMarkdown } from "../ui/SafeMarkdown";
import { conversationQueryKey } from "./conversation";
import type { BlockProps } from "./registry";

/** The scope of a note in words: "the whole discipline", or what it is about. */
function scopeWords(scope: unknown): string {
  if (!scope || typeof scope !== "object") return "the whole discipline";
  const parts = Object.entries(scope as Record<string, unknown>)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key, value]) => `${key.replace(/_/g, " ")} ${String(value)}`);
  return parts.length > 0 ? `the whole discipline, about ${parts.join(", ")}` : "the whole discipline";
}

/**
 * A discipline note the agent offers (completion round, CP-11): the draft, and Keep it or Not now. Keep it may change the
 * wording first. The buttons are the commands that already exist (`confirm_discipline_note`, `drop_discipline_note`);
 * an engineer keeps a note, so another role sees the server's refusal in plain words.
 */
export function NoteOfferBlockView({ block, context }: BlockProps<NoteOfferBlock>) {
  const queryClient = useQueryClient();
  const confirm = useApiCommand("confirm_discipline_note");
  const drop = useApiCommand("drop_discipline_note");
  const [status, setStatus] = useState(block.status);
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(block.draft);
  const [problem, setProblem] = useState<string | null>(null);
  const busy = confirm.isPending || drop.isPending;

  const settle = async (keep: boolean) => {
    setProblem(null);
    try {
      if (keep) {
        const wording = text.trim();
        await confirm.mutateAsync({ id: block.offer_id, ...(wording && wording !== block.draft ? { text: wording } : {}) });
      } else {
        await drop.mutateAsync({ id: block.offer_id });
      }
      setStatus(keep ? "confirmed" : "dropped");
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ["discipline_notes"] });
      void queryClient.invalidateQueries({ queryKey: conversationQueryKey(context.conversationKey, context.admin) });
    } catch (error) {
      setProblem(refusalText(error));
    }
  };

  return (
    <section className="agent-note-offer" aria-label="A note for the discipline">
      <p className="muted">The agent offers a note for {scopeWords(block.scope)}:</p>
      {editing ? (
        <label className="field">
          <span className="field__label">The wording of the note</span>
          <textarea className="field__input" rows={3} value={text} onChange={(event) => setText(event.target.value)} disabled={busy} />
        </label>
      ) : (
        <div className="agent-text" data-agent="true">
          <SafeMarkdown>{status === "confirmed" ? text : block.draft}</SafeMarkdown>
        </div>
      )}
      {status === "offered" ? (
        <div className="row">
          <Button variant="primary" busy={confirm.isPending} disabled={busy || !text.trim()} onClick={() => void settle(true)}>
            Keep it
          </Button>
          {!editing && (
            <Button disabled={busy} onClick={() => setEditing(true)}>
              Change the wording first
            </Button>
          )}
          <Button variant="quiet" busy={drop.isPending} disabled={busy} onClick={() => void settle(false)}>
            Not now
          </Button>
        </div>
      ) : (
        <p className="muted" role="status">
          {status === "confirmed" ? "Kept as a discipline note. The agent keeps it in mind for everyone." : "Not kept."}
        </p>
      )}
      {problem && (
        <p role="alert" className="form-error">
          {problem}
        </p>
      )}
    </section>
  );
}
